import React, { FunctionComponent, ReactNode, useRef, useState } from 'react';
import { Button, Card, Col, Collapse, Form, Row, Typography } from 'antd';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  AiAssetStyles,
  AssetOrigin,
  DayOfWeek,
  DEFAULT_TIMEZONE,
  defaultThumbnailConfig,
  defaultTikTokSlots,
  optimalPostingTimes,
  Series,
  SeriesPostingSchedule,
  SeriesSubject,
  SocialAccount,
  SocialProvider,
  Timezone,
  TimezoneLabels,
} from '@monorepo/types';
import './series-form.scss';
import BaseVideoForm, {
  BaseVideoFormType,
  SectionTitle,
} from '../base-video-form/base-video-form';
import {
  CaretRightOutlined,
  FieldNumberOutlined,
  FieldTimeOutlined,
  GlobalOutlined,
  SendOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useStore } from '../../helpers/use-store';
import {
  FormSelect,
  FormSlider,
  LoadingState,
  useLoading,
} from '@monorepo/react-components';
import { FormSocialSelect } from './form-social-select';
import { compact, replace, startCase } from 'lodash';
import SubscribeCTA from '../../components/subscribe-cta/subscribe-cta';
import { SeriesOptionsForm } from './series-settings';
import PostingScheduleModal, {
  PostingScheduleModalActions,
} from './series-schedule/series-schedule';

const defaultPostingTimes: SeriesPostingSchedule = Object.values(
  DayOfWeek
).reduce((acc, day) => {
  acc[day] = [...defaultTikTokSlots]; // copy the array to avoid mutation
  return acc;
}, {} as SeriesPostingSchedule);

const { Text } = Typography;

const iconStyle = {
  fontSize: '20px',
  color: '#1890ff',
};
const sections = ['duration', 'social', 'videosPerDay', 'timezone'];

function getExtraContentSection(t: TFunction, form: UseFormReturn<Series>) {
  return (
    <FormSelect
      style={{ marginBottom: '15px' }}
      form={form}
      defaultValue={SeriesSubject.RandomAIStory}
      placeholder={t('seriesForm.subjectPlaceholder')}
      controllerProps={{
        name: 'subjectType',
        rules: { required: t('seriesForm.subjectRequired') },
      }}
      options={Object.values(SeriesSubject).map((subject) => {
        return {
          label: t(`seriesForm.subject.${subject}`, {
            defaultValue: startCase(replace(subject, '-', ' ')),
          }),
          value: subject,
        };
      })}
    />
  );
}

const getSectionsText = (
  t: TFunction,
  form: UseFormReturn<Series>,
  socialAccounts?: SocialAccount[]
) => {
  const socialSectionText =
    form.getValues('socialAccountIds') && socialAccounts
      ? socialAccounts.map((account) => startCase(account?.provider)).join(',')
      : '';
  const durationSectionText = form.getValues('duration')
    ? t('seriesForm.durationSectionText', {
        seconds: form.getValues('duration'),
      })
    : '';
  const videosPerDaySectionText = form.getValues('videosPerDay')
    ? t('seriesForm.videosPerDaySectionText', {
        count: form.getValues('videosPerDay'),
      })
    : '';
  return { socialSectionText, durationSectionText, videosPerDaySectionText };
};

interface Props {
  collapsed?: boolean;
  sectionsText?: boolean;
  loadingButtonText?: ReactNode;
  series?: Series;
  onSubmit: (date: Partial<Series>) => Promise<void>;
  useCollapse?: boolean;
}

const SeriesForm: FunctionComponent<Props> = ({
  collapsed,
  series,
  onSubmit,
  sectionsText,
  loadingButtonText,
  useCollapse = true,
}) => {
  const { t } = useTranslation();
  const scheduleModalRef = useRef<PostingScheduleModalActions>(null);
  const {
    dataStore: {
      voiceoverStore,
      userStore,
      assetStore,
      socialAccountStore,
      seriesStore,
    },
  } = useStore();

  const [expandedSection, setExpandedSections] = useState<string[]>(
    collapsed ? [] : sections
  );
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);

  const socialAccountIds =
    series?.socialAccountIds && series?.socialAccountIds.length > 0
      ? series?.socialAccountIds
      : compact([series?.socialAccountId]);

  const form = useForm<Series>({
    mode: 'all',
    defaultValues: {
      subjectType: SeriesSubject.RandomAIStory,
      assetOrigin: AssetOrigin.AIImages,
      assetStyle: AiAssetStyles.CINEMATIC,
      timezone: DEFAULT_TIMEZONE,
      language: 'en',
      duration: 60,
      postingSchedule: defaultPostingTimes,
      videosPerDay: 1,
      voiceoverId:
        voiceoverStore.items.find((voiceover) => voiceover.name === 'alloy')
          ?._id || '',
      ...series,
      socialAccountIds,
      thumbnailConfig: {
        ...defaultThumbnailConfig,
        enabled: true,
        enableVoiceover: true,
        ...(series?.thumbnailConfig || {}),
      },
    },
  });

  const _onSubmit = async (data: Partial<Series> & { audioFile?: File }) => {
    try {
      updateLoadingState(LoadingState.Loading);

      // If an audio file was selected, upload it before saving the series
      if (data.audioFile instanceof File) {
        const asset = await assetStore.upload({
          file: data.audioFile,
        });

        data.backgroundMusicUrl = asset.url;
        data.backgroundMusicId = '';
        delete data.audioFile; // don’t send raw File
      }

      await onSubmit(data);

      userStore.getLatestUser();
    } catch (e) {
      console.error(`failed series submit`, e);
      throw e;
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const selectedVideosLength = form.watch('duration');
  const seriesSubject = form.watch('subjectType');
  const socialAccounts: SocialAccount[] = compact(
    socialAccountIds.map((id) => socialAccountStore.get(id))
  );
  const { socialSectionText, durationSectionText, videosPerDaySectionText } =
    getSectionsText(t, form, socialAccounts);

  const frequencyAllowed = [1, 2, 3];

  const isLoading = loadingState === LoadingState.Loading;

  const isUpdate = Boolean(series?._id);

  const isSubscriptionAmountGood = isUpdate
    ? userStore.userSubscription.allowedSeriesAmount >= seriesStore.items.length
    : userStore.userSubscription.allowedSeriesAmount > seriesStore.items.length;

  const isAllowedToCreate =
    userStore.isSubscriptionActive && isSubscriptionAmountGood;

  const getSocialTypes = (): SocialProvider[] => {
    const currentSocialAccountIds = form.getValues('socialAccountIds') || [];

    if (currentSocialAccountIds.length === 0) {
      return [SocialProvider.TikTok, SocialProvider.YouTube];
    }

    const availablePlatforms = [SocialProvider.TikTok, SocialProvider.YouTube];

    const existingSocialAccounts = compact(
      currentSocialAccountIds.map((id) => socialAccountStore.get(id))
    );

    const existingPlatforms = existingSocialAccounts.map(
      (account) => account.provider
    );

    return availablePlatforms.filter(
      (platform) => !existingPlatforms.includes(platform)
    );
  };

  const availableSocialTypes = getSocialTypes();

  const formItems = [
    {
      key: 'duration',
      label: (
        <SectionTitle
          icon={<FieldTimeOutlined style={iconStyle} />}
          text={t('seriesForm.videoLengthTitle')}
        />
      ),
      extra: sectionsText && (
        <Text type="secondary">{durationSectionText}</Text>
      ),
      children: (
        <FormSlider
          label={t('seriesForm.videoLengthLabel', {
            seconds: selectedVideosLength,
          })}
          form={form}
          props={{
            step: 5,
            min: 30,
          }}
          controllerProps={{
            name: 'duration',
            rules: {
              min: {
                value: 30,
                message: t('seriesForm.videoLengthMin'),
              },
              max: {
                value: 90,
                message: t('seriesForm.videoLengthMax'),
              },
            },
          }}
        />
      ),
    },
    {
      key: 'social',
      label: (
        <SectionTitle
          icon={<SendOutlined style={iconStyle} />}
          text={t('seriesForm.socialAccountsTitle')}
        />
      ),
      extra: sectionsText && <Text type="secondary">{socialSectionText}</Text>,
      children: (
        <>
          <FormSocialSelect
            socialTypes={availableSocialTypes}
            form={form}
            controllerProps={{
              name: 'socialAccountIds',
            }}
            onChange={(socialAccountIds: string[]) => {
              form.setValue('socialAccountIds', socialAccountIds);
              form.trigger('socialAccountIds');
            }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {t('seriesForm.socialAccountsEmpty')}
          </Text>
        </>
      ),
    },
    {
      key: 'videosPerDay',
      label: (
        <SectionTitle
          icon={<FieldNumberOutlined style={iconStyle} />}
          text={t('seriesForm.postingFrequencyTitle')}
        />
      ),
      extra: sectionsText && (
        <Text type="secondary">{videosPerDaySectionText}</Text>
      ),
      children: (
        <>
          <FormSelect
            form={form}
            placeholder={t('seriesForm.frequencyPlaceholder')}
            controllerProps={{
              name: 'videosPerDay',
              rules: {
                required: t('seriesForm.frequencyRequired'),
              },
            }}
            options={frequencyAllowed.map((videosPerDay) => ({
              value: videosPerDay,
              label:
                videosPerDay > 1
                  ? t('seriesForm.frequencyOptionPlural', {
                      count: videosPerDay,
                    })
                  : t('seriesForm.frequencyOptionSingular', {
                      count: videosPerDay,
                    }),
            }))}
          />
          <Button
            style={{ marginTop: '10px' }}
            type="dashed"
            block
            onClick={() => scheduleModalRef.current?.open()}
          >
            {t('seriesForm.manualSchedule')}
          </Button>
          <PostingScheduleModal
            ref={scheduleModalRef}
            form={form}
            videosPerDay={form.watch('videosPerDay') || 1}
          />
        </>
      ),
    },
    {
      key: 'timezone',
      label: (
        <SectionTitle
          icon={<GlobalOutlined style={iconStyle} />}
          text={t('seriesForm.timezoneTitle')}
        />
      ),
      extra: sectionsText && (
        <Text type="secondary">
          {TimezoneLabels[form.getValues('timezone')]}
        </Text>
      ),
      children: (
        <FormSelect
          form={form}
          placeholder={t('seriesForm.timezonePlaceholder')}
          controllerProps={{
            name: 'timezone',
          }}
          options={Object.entries(Timezone).map(([key, value]) => ({
            value: value,
            label: TimezoneLabels[value],
          }))}
        />
      ),
    },
    {
      key: 'seriesOptions',
      label: (
        <SectionTitle
          icon={<SettingOutlined style={iconStyle} />}
          text={t('seriesForm.seriesSettingsTitle')}
        />
      ),
      children: <SeriesOptionsForm form={form} />,
    },
  ];

  return (
    <Form
      className={'series-form'}
      layout="vertical"
      onFinish={form.handleSubmit(_onSubmit)}
    >
      <Row justify={'center'}>
        <Col span={24}>
          <Card>
            <BaseVideoForm
              sectionText={sectionsText}
              extraContentSection={getExtraContentSection(t, form)}
              form={form as unknown as UseFormReturn<BaseVideoFormType>}
              hideScript={
                !series?._id && seriesSubject !== SeriesSubject.Custom
              }
              collapsed={collapsed}
              useCollapse={useCollapse}
            />

            {useCollapse ? (
              <Collapse
                className="custom-collapse"
                activeKey={expandedSection}
                expandIcon={({ isActive }) => {
                  return (
                    <Button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        padding: 0,
                        border: 'none',
                      }}
                    >
                      <CaretRightOutlined
                        style={{ fontSize: '14px', marginTop: '2px' }}
                        rotate={isActive ? 90 : 0}
                      />
                    </Button>
                  );
                }}
                bordered={false}
                onChange={(keys) => setExpandedSections(keys)}
                items={formItems}
              />
            ) : (
              <div className="custom-collapse form-item">
                {formItems.map((item) => (
                  <div key={item.key} style={{ marginBottom: '24px' }}>
                    <div
                      className={'form-item-title'}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '16px',
                      }}
                    >
                      {item.label}
                      {item.extra}
                    </div>
                    {item.children}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col span={24} style={{ marginTop: 20 }}>
          <Form.Item>
            <div className={'create-button'}>
              <Button
                loading={isLoading}
                disabled={!form.formState.isValid || !isAllowedToCreate}
                icon={
                  <ThunderboltOutlined
                    style={{ fontSize: '18px', marginTop: '2px' }}
                  />
                }
                type="primary"
                htmlType="submit"
                size="large"
                block
              >
                {series
                  ? t('seriesForm.updateButton')
                  : t('seriesForm.createButton')}
              </Button>
              {!isAllowedToCreate ? <SubscribeCTA /> : null}
              {isLoading && loadingButtonText ? loadingButtonText : null}
            </div>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default SeriesForm;
